/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  useTheme,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { PROVIDERS, PROVIDER_META } from "../model/types";
import { getProviderConfig, setProviderConfig, saveProviderKey } from "../model/assistantClient";
import { AI_ACCENT } from "../model/tokens";

/**
 * Bring-your-own-key settings.
 *
 * Lets a tenant choose which model provider the backend routes to — Vecros default,
 * or their own Claude / ChatGPT key. The provider CHOICE is stored locally (just an id);
 * the API KEY is sent to the backend to be stored encrypted and used server-side. The
 * raw key is never persisted in the browser.
 */
export default function AssistantSettingsModal({ open, onClose }) {
  const theme = useTheme();
  const [provider, setProvider] = useState(getProviderConfig().provider);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const meta = PROVIDER_META[provider] || {};

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      setProviderConfig(provider);
      if (meta.byok && apiKey.trim()) {
        await saveProviderKey(provider, apiKey.trim());
      }
      setSaved(true);
      setApiKey(""); // never keep the raw key around
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError("Couldn't save your key. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const optionCard = (id) => {
    const m = PROVIDER_META[id];
    const selected = provider === id;
    return (
      <Box
        key={id}
        role="button"
        onClick={() => setProvider(id)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          borderRadius: "10px",
          cursor: "pointer",
          border: `1.5px solid ${selected ? AI_ACCENT : theme.palette.border.color}`,
          backgroundColor: selected ? "rgba(59,110,246,0.06)" : "transparent",
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: `2px solid ${selected ? AI_ACCENT : theme.palette.border.color}`,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          {selected && <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: AI_ACCENT }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.palette.text.color }}>
            {m.label}
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText.color }}>
            {m.byok ? "Uses your own API key (billed to you)" : "Managed by Vecros — no key needed"}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Modal open={open} onClose={onClose} aria-label="Assistant settings">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(440px, calc(100vw - 32px))",
          backgroundColor: theme.palette.secondary.main,
          border: `1px solid ${theme.palette.border.color}`,
          borderRadius: "16px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
          outline: "none",
          p: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 600, color: theme.palette.text.color }}>
            AI Assistant
          </Typography>
          <Box role="button" aria-label="Close" onClick={onClose} sx={{ ml: "auto", display: "inline-flex", cursor: "pointer", color: theme.palette.secondaryText.color }}>
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: 13, color: theme.palette.secondaryText.color, mb: 2 }}>
          Choose which model powers your assistant. Bring your own key to use Claude or ChatGPT.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[PROVIDERS.VECROS, PROVIDERS.ANTHROPIC, PROVIDERS.OPENAI, PROVIDERS.GOOGLE].map(optionCard)}
        </Box>

        {meta.byok && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", mb: 0.75 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.color }}>
                {meta.label.split(" — ")[0]} API key
              </Typography>
              {meta.keyUrl && (
                <Typography
                  component="a"
                  href={meta.keyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ml: "auto", fontSize: 12, fontWeight: 600, color: AI_ACCENT, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  Don't have one? Get a key ↗
                </Typography>
              )}
            </Box>
            <TextField
              type="password"
              fullWidth
              size="small"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={meta.placeholder || "Paste your API key"}
              autoComplete="off"
              inputProps={{ "aria-label": "API key" }}
            />
            <Typography sx={{ fontSize: 11, color: theme.palette.secondaryText.color, mt: 0.75 }}>
              Your key is sent securely to our backend, stored encrypted, and used only to call
              the model on your behalf. It is never stored in your browser.
            </Typography>
          </Box>
        )}

        {error && (
          <Typography sx={{ fontSize: 12, color: theme.palette.error?.main || "#CF4E36", mt: 1.5 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2.5 }}>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            sx={{ backgroundColor: AI_ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#2f5fd6" } }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
          </Button>
          {saved && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#2C9B67" }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13 }}>Saved</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
